import React from 'react';
import {
  Box,
  Button,
  Drawer,
  Menu,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {useLocation, useNavigate} from 'react-router-dom';
import ChangeWithZone from '../../../hoc/ChangeWithZone';

import '../HeaderAnalysis.styles.css';
import {Modal} from "../../../components/atoms/Modal";
import LeavePageDialog from "../../../components/organisms/LeavePageDialog/LeavePageDialog";
import Stack from "@mui/material/Stack";
import {Pagination} from "../../../components/atoms/Pagination";
import {PageHeader} from "../../../components/organisms/PageHeader";
import {Toast} from "../../../components/atoms/Toast";
import {HeaderAnalysisInfoBanner} from './HeaderAnalysisInfoBanner';
import {HeaderAnalysisListToolbar} from './HeaderAnalysisListToolbar';
import {HeaderAnalysisRulesTable} from './HeaderAnalysisRulesTable';
import {useHeaderAnalysisListController} from './useHeaderAnalysisListController';

export {HEADER_ANALYSIS_MIN_ROW_HEIGHT} from './HeaderAnalysisRulesTable';


export {
  isHeaderAnalysisRuleReadOnly,
  mapPersistedHeaderAnalysisRule,
} from '../headerAnalysisPresentation';

type HeaderAnalysisPageProps = {
  currentZone?: number | string | null;
  globalZone?: boolean;
};

const HeaderAnalysisPage: React.FC<HeaderAnalysisPageProps> = ({globalZone = false}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const threatScoreRulesBasePath = location.pathname.startsWith('/settings/threat-scores')
    ? '/settings/threat-scores'
    : '/threat-scores';
  const {
    actionsOpen,
    anchorEl,
    changeStatusRule,
    checked,
    confirmNavigation,
    cancelNavigation,
    currentPage,
    dropdownFilterOpenHandler,
    endPoint,
    filterDropdownOpen,
    handleActionDelete,
    handleCancelWhenUnsaved,
    handleClearFilter,
    handleCloseModal,
    handleContinueWhenUnsaved,
    handleDeleteEntry,
    handleDiscard,
    handleFilter,
    handleKeywordFocus,
    handlePageChange,
    handlePerPageRowsChange,
    handleSaveChange,
    hideActionsDropdown,
    isActionShow,
    isReadOnlyRule,
    isShowLoading,
    loadingPage,
    onSearchInboxByKeywords,
    onSort,
    openActionsDropdown,
    openDelete,
    openUnsavedConfirm,
    pendingFilterSearchKeyword,
    pendingKeywordRef,
    perPages,
    prepareActionEdit,
    resetFilters,
    ruleRows,
    selectedItems,
    setActionShow,
    setPendingFilterSearchKeyword,
    showPrompt,
    sortModel,
    startPoint,
    t,
    toastValue,
    totalPages,
    totalRows,
  } = useHeaderAnalysisListController(globalZone);

  const paginationComponent = () => (
    <Pagination
      itemsPerPage={perPages}
      currentPage={currentPage}
      startPoint={startPoint}
      endPoint={endPoint}
      maxPage={totalPages}
      totalItems={totalRows}
      handlePerRowsChange={handlePerPageRowsChange}
      handlePageChange={handlePageChange}
    />
  );

  const saveChangeUI = (
    <Box className={'save-change-content'}
      sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '15px 0'}}
    >
      <div className={'btn discard-btn'}>
        <Button variant="outlined" onClick={handleDiscard}>{t('actions.discard')}</Button>
      </div>
      <div className={'btn save-btn'}>
        <Button variant="contained" onClick={handleSaveChange}>{t('actions.saveChanges')}</Button>
      </div>
    </Box>
  );

  const handleActionEdit = (row?: any) => {
    const ruleId = prepareActionEdit(row);
    if (ruleId === undefined) return;
    navigate(`${threatScoreRulesBasePath}/rules/${ruleId}`);
  };

  return (
      <div className="header-analysis-page page-content-wrapper">
        <PageHeader
            pageTitle={'Threat Score Rules'}
            infoPage={t('tooltips.templates.headerAnalysisHeader')}
            tooltipStyle={{ maxWidth: '650px', padding:"8px"}}
            children={
              <Button
                  className="new-entry-btn"
                  data-testid="new-entry-btn"
                  onClick={() => navigate(`${threatScoreRulesBasePath}/rules/new`)}
                  variant="contained"
                  size="large"
              >
                <AddIcon
                    style={{ fontSize: '20px' }}/>
                <span style={{ paddingLeft: '8px' }}>{'New Rule'}</span>
              </Button>
            }
        />
        <Stack className="page-content-body">
          <div className="header-analysis-content">
            <HeaderAnalysisInfoBanner/>
            <HeaderAnalysisListToolbar
              selectedItems={selectedItems}
              resetFilter={resetFilters}
              handleEnterSearch={onSearchInboxByKeywords}
              onFilter={handleFilter}
              searchInputValue={pendingFilterSearchKeyword}
              setSearchInputValue={setPendingFilterSearchKeyword}
              onClearFilter={handleClearFilter}
              filtersOpen={filterDropdownOpen}
              setFiltersOpen={dropdownFilterOpenHandler}
              handleKeywordFocus={handleKeywordFocus}
              searchFilterRef={pendingKeywordRef}
            />
            <div className="header-analysis-datatable" data-row-height-mode="auto">
              <HeaderAnalysisRulesTable
                data={ruleRows}
                checked={checked}
                isReadOnlyRule={isReadOnlyRule}
                changeStatusRule={changeStatusRule}
                onOpenActions={openActionsDropdown}
                onRowDoubleClick={(params: any) => handleActionEdit(params.row)}
                paginationComponent={paginationComponent}
                loadingTable={loadingPage}
                onSortModelChange={(model: any) => onSort(model)}
                sortModel={sortModel}
              />
              <Menu
                  id="header-analysis-actions-customized-menu"
                  data-testid="actions-customized-menu"
                  MenuListProps={{'aria-labelledby': 'actions-customized-button'}}
                  anchorEl={anchorEl}
                  open={actionsOpen}
                  onClose={hideActionsDropdown}
                  anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                  transformOrigin={{vertical: 'top', horizontal: 'right'}}
              >
                <div className="actions-rules for-email">
                  <ul>
                    <li onClick={() => handleActionEdit()}>Edit</li>
                    <li onClick={() => handleActionDelete()}>Delete</li>
                  </ul>
                </div>
              </Menu>
            </div>
          </div>
        </Stack>
        <Drawer
            variant="persistent"
            anchor="bottom"
            open={isActionShow}
            onClose={() => setActionShow(false)}
            className={'save-change-container'}
            children={saveChangeUI}
        ></Drawer>
        <Toast
            isOpen={toastValue.isOpen}
            type={toastValue.type}
            message={toastValue.message}
            toastId={toastValue.toastId}
            autoHideDuration={2000}
        />
        <LeavePageDialog
            showDialog={showPrompt}
            confirmNavigation={confirmNavigation}
            cancelNavigation={cancelNavigation}
        />
        <Modal
            idModal="modal-delete-header-analysis"
            openDialog={openDelete}
            variant="delete"
            title="Delete entry"
            content="Are you sure to delete this entry?"
            actionAgree={/* istanbul ignore next */() => {
              handleDeleteEntry()
              /* istanbul ignore next */
              handleCloseModal()
            }}
            actionCancel={handleCloseModal}
            textAgree="Delete"
            onClose={handleCloseModal}
        />
        <Modal
            idModal="modal-unsaved-confirmation"
            openDialog={openUnsavedConfirm}
            variant="warning"
            title={t('pageTitles.threatExceptions.unsavedChanges')}
            content={t('messages.threatExceptions.unsavedChanges')}
            actionAgree={handleContinueWhenUnsaved}
            actionCancel={handleCancelWhenUnsaved}
            textAgree={t('actions.threatExceptions.discardChanges')}
            textCancel={t('actions.cancel')}
            onClose={handleCancelWhenUnsaved}
        />
      </div>
  );
};

export const HeaderAnalysis = ChangeWithZone(HeaderAnalysisPage);
